let csrfToken = '';

export class ApiError extends Error {
  public constructor(public readonly code: string, message: string, public readonly status: number) {
    super(message);
  }
}

export function setCsrfToken(value: string): void {
  csrfToken = value;
}

/**
 * Un échec doit toujours dire quelque chose d'exploitable. Quand le serveur ne
 * renvoie pas de message (réponse non JSON d'un proxy, coupure, 502 pendant un
 * redéploiement), le code HTTP est affiché plutôt qu'un « la requête a échoué »
 * qui n'aide personne à diagnostiquer — constaté en recette le 17/08/2026, où
 * plusieurs symptômes différents se présentaient sous ce même texte.
 */
function fallbackMessage(status: number): string {
  if (status === 0) return 'Serveur injoignable. Vérifiez votre connexion.';
  if (status === 401) return 'Session expirée. Reconnectez-vous.';
  if (status === 403) return 'Action refusée par le serveur (droits ou jeton de sécurité).';
  if (status === 413) return 'Fichier trop volumineux.';
  if (status === 429) return 'Trop de tentatives. Patientez quelques minutes.';
  if (status >= 500) return `Le serveur a répondu une erreur ${status}. Réessayez dans un instant.`;
  return `La requête a échoué (code ${status}).`;
}

async function parse<T>(response: Response): Promise<T> {
  const content = await response.json().catch(() => ({})) as { error?: { code?: string; message?: string } };
  if (!response.ok) {
    throw new ApiError(
      content.error?.code ?? 'REQUEST_FAILED',
      content.error?.message ?? fallbackMessage(response.status),
      response.status,
    );
  }
  return content as T;
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (options.method && !['GET', 'HEAD'].includes(options.method.toUpperCase()) && csrfToken) {
    headers.set('x-csrf-token', csrfToken);
  }
  const response = await fetch(path, { ...options, headers, credentials: 'same-origin' });
  return parse<T>(response);
}

